<script lang="ts">
	import { goto } from '$app/navigation';
	import { Button } from '$lib/components/ui/button';
	import Heart from 'lucide-svelte/icons/heart';
	import HeartCrack from 'lucide-svelte/icons/heart-crack';
	import Loader from 'lucide-svelte/icons/loader';
	import { toast } from 'svelte-sonner';
	import { authClient } from '$lib/auth-client';
	import type { StoryImages } from '$lib/types/story';

	let { title, blurb, storySlug, images, labels, isInLibrary } = $props<{
		title: string;
		blurb: string;
		storySlug: string;
		images: StoryImages;
		labels: string[];
		isInLibrary: boolean;
	}>();

	let clientSideSession = authClient.useSession();
	let localIsInLibrary = $state(isInLibrary);
	let localIsSaving = $state(false);

	async function saveToLibrary() {
		if (!$clientSideSession.data?.user) {
			goto('/signin');
			return;
		}

		localIsSaving = true;
		try {
			const action = localIsInLibrary ? 'remove' : 'add';
			const response = await fetch('/api/library/toggle', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					slug: storySlug,
					action
				})
			});

			if (!response.ok) {
				throw new Error('Failed to update library');
			}

			const { isInLibrary: newState } = await response.json();
			localIsInLibrary = newState;

			toast(localIsInLibrary ? 'Added to Library' : 'Removed from Library', {
				description: localIsInLibrary
					? `${title} has been added to your library`
					: `${title} has been removed from your library`,
				duration: 3000
			});
		} catch (err) {
			console.error('Error updating library:', err);
			toast.error('Error', {
				description: 'Failed to update library. Please try again.',
				duration: 3000
			});
		} finally {
			localIsSaving = false;
		}
	}
</script>

<!-- Main container with two distinct sections -->
<div class="animate-fade-in flex min-h-screen flex-col">
	<!-- Hero Image Section (top half) -->
	<div class="relative h-[20vh] w-full md:h-[30vh] lg:h-[50vh]">
		<img
			src={images.fullSize}
			srcset="{images.cover320} 320w, {images.cover640} 640w, {images.cover960} 960w, {images.cover1440} 1440w, {images.cover1920} 1920w"
			alt={title}
			class="h-full w-full object-cover"
			loading="eager"
			decoding="async"
		/>
		<div class="absolute inset-0">
			<!-- Bottom gradient for smooth transition -->
			<div
				class="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"
			></div>

			<!-- Left gradient for smooth transition -->
			<div
				class="absolute inset-0 bg-gradient-to-r from-background via-transparent to-transparent"
			></div>

			<!-- Right gradient for smooth transition -->
			<div
				class="absolute inset-0 bg-gradient-to-l from-background via-transparent to-transparent"
			></div>
		</div>
	</div>

	<!-- Content Section (bottom half) -->
	<div class="relative w-full bg-background px-6 py-12 md:px-12 lg:px-16">
		<div class="animate-slide-up mx-auto max-w-3xl">
			<!-- Labels -->
			<div class="flex flex-wrap gap-2">
				{#each labels as label}
					<span class="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
						{label}
					</span>
				{/each}
			</div>

			<!-- Title -->
			<h1 class="mt-4 text-4xl font-bold text-primary md:text-5xl lg:text-6xl">
				{title}
			</h1>

			<!-- Blurb -->
			<p class="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base lg:text-lg">
				{#if blurb.length > 300}
					{blurb.slice(0, 300)}...
				{:else}
					{blurb}
				{/if}
			</p>

			<!-- Buttons -->
			<div class="mt-8 flex flex-wrap items-center gap-4">
				<Button href={`/story/${storySlug}/page/1`} size="lg" class="gap-2">
					Begin Reading
					<span class="i-lucide-book-open"></span>
				</Button>

				<Button
					variant="outline"
					size="lg"
					class="gap-2"
					disabled={localIsSaving}
					onclick={saveToLibrary}
				>
					{#if localIsSaving}
						<Loader class="h-5 w-5 animate-spin" />
					{:else if localIsInLibrary}
						Remove from Library
						<HeartCrack class="h-5 w-5" />
					{:else}
						Save to Library
						<Heart class="h-5 w-5" />
					{/if}
				</Button>
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	@keyframes fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes slide-up {
		from {
			transform: translateY(20px);
		}
		to {
			transform: translateY(0);
		}
	}

	.animate-fade-in {
		animation: fade-in 0.5s ease-out;
	}

	.animate-slide-up {
		animation: slide-up 0.5s ease-out;
	}
</style>
